/**
 * Diálogos de Clientes APP (`/gestion/clientes-app`).
 *
 * Puerto de `ClientesDiscount::{crear_vehiculo_bd, eliminar_vehiculo_bd,
 * filtrar_descuentos, eliminar_descuentos_masivo}` y del alta de descuentos del
 * panel anterior, ahora contra las tablas locales `customer_vehicles` y
 * `customer_discounts`.
 *
 * Reglas conservadas:
 * - La placa no se repite entre vehículos activos: si ya está registrada, el
 *   servicio responde con el nombre del cliente dueño.
 * - El descuento por **placa** exige vehículo; el de **consumo** exige rango de
 *   consumo (mínimo y máximo).
 */

import { Globe, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import { useConsultaDocumento } from './clientes.api';
import { useCatalogosClientesApp } from './cierre.api';
import { fechaCorta } from './gestion-formato';
import type {
  ClienteApp,
  ClienteAppGeneral,
  DatosClienteApp,
  DatosDescuentoApp,
  DescuentoApp,
  VehiculoApp,
  VehiculoAppDetalle,
} from './cierre-gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const TIPOS = [
  { clave: 'document', label: 'Por documento' },
  { clave: 'license_plate', label: 'Por placa' },
  { clave: 'consumption', label: 'Por consumo' },
];

/** Mensaje legible de un error de la API. */
function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return 'Inténtalo de nuevo en unos segundos.';
}

/** Nombre visible de un cliente de la APP. */
function nombreCliente(cliente: ClienteApp | undefined, id: number): string {
  if (!cliente) return `Cliente #${id}`;

  return cliente.nombre_comercial || cliente.nombre;
}

/* ------------------------------------------------------------------ *
 * Alta de vehículo
 * ------------------------------------------------------------------ */

interface DialogoVehiculoProps {
  open: boolean;
  onClose: () => void;
  guardando: boolean;
  error: unknown;
  onGuardar: (clienteId: number, datos: {
    license_plate: string;
    vehicle_type: string;
    brand: string;
    model: string;
    year: number | null;
  }) => void;
}

/** Alta de un vehículo de la APP para un cliente. */
export function DialogoNuevoVehiculoApp({
  open,
  onClose,
  guardando,
  error,
  onGuardar,
}: DialogoVehiculoProps) {
  const catalogos = useCatalogosClientesApp();
  const [clienteId, setClienteId] = useState('');
  const [placa, setPlaca] = useState('');
  const [tipo, setTipo] = useState('');
  const [marca, setMarca] = useState('');
  const [modelo, setModelo] = useState('');
  const [anio, setAnio] = useState('');

  const clientes = catalogos.data?.clientes ?? [];

  const reiniciar = () => {
    setClienteId('');
    setPlaca('');
    setTipo('');
    setMarca('');
    setModelo('');
    setAnio('');
    onClose();
  };

  const puedeGuardar = Boolean(clienteId) && placa.trim().length > 0 && !guardando;

  return (
    <Dialog
      open={open}
      onClose={reiniciar}
      title="Nuevo vehículo de la APP"
      description="Registra la placa que usará el cliente en la aplicación para sus descuentos."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={reiniciar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() =>
              onGuardar(Number(clienteId), {
                license_plate: placa.trim().toUpperCase(),
                vehicle_type: tipo.trim().toUpperCase(),
                brand: marca.trim().toUpperCase(),
                model: modelo.trim().toUpperCase(),
                year: anio ? Number(anio) : null,
              })
            }
          >
            Registrar vehículo
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Alert variant="error" title="No se pudo registrar el vehículo">{mensajeDeError(error)}</Alert> : null}

        <label className="flex flex-col gap-1 text-xs text-ink-500">
          Cliente
          <Select
            className={CONTROL}
            value={clienteId}
            onChange={(evento) => setClienteId(evento.target.value)}
            disabled={catalogos.isLoading}
            searchable
          >
            <option value="">Elige el cliente…</option>
            {clientes.map((cliente) => (
              <option key={cliente.id} value={String(cliente.id)} data-search={cliente.numero_doc}>
                {nombreCliente(cliente, cliente.id)}
                {cliente.numero_doc ? ` · ${cliente.numero_doc}` : ''}
              </option>
            ))}
          </Select>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Placa"
            hint="No puede repetirse entre vehículos activos."
            value={placa}
            onChange={(evento) => setPlaca(evento.target.value.toUpperCase())}
          />
          <Input
            label="Tipo de vehículo"
            hint="Por ejemplo CAMIÓN, TRACTO o NO IDENTIFICADO."
            value={tipo}
            onChange={(evento) => setTipo(evento.target.value.toUpperCase())}
          />
          <Input label="Marca" value={marca} onChange={(evento) => setMarca(evento.target.value)} />
          <Input label="Modelo" value={modelo} onChange={(evento) => setModelo(evento.target.value)} />
          <Input
            label="Año"
            type="number"
            value={anio}
            onChange={(evento) => setAnio(evento.target.value)}
            containerClassName="sm:col-span-2"
          />
        </div>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Alta y edición de descuento
 * ------------------------------------------------------------------ */

interface DialogoDescuentoProps {
  open: boolean;
  descuento: DescuentoApp | null;
  onClose: () => void;
  guardando: boolean;
  error: unknown;
  onGuardar: (datos: DatosDescuentoApp) => void;
}

/** Alta o edición de un descuento de la APP. */
export function DialogoDescuentoApp({
  open,
  descuento,
  onClose,
  guardando,
  error,
  onGuardar,
}: DialogoDescuentoProps) {
  const catalogos = useCatalogosClientesApp();

  const [clienteId, setClienteId] = useState(() => String(descuento?.customer_id ?? ''));
  const [tipo, setTipo] = useState(() => descuento?.discount_type ?? 'document');
  const [vehiculoId, setVehiculoId] = useState(() => String(descuento?.vehicle_id ?? ''));
  const [gradoId, setGradoId] = useState(() => String(descuento?.fuel_grade_id ?? ''));
  const [importe, setImporte] = useState(() => String(descuento?.discount_amount ?? ''));
  const [minimo, setMinimo] = useState(() => String(descuento?.min_consumption ?? ''));
  const [maximo, setMaximo] = useState(() => String(descuento?.max_consumption ?? ''));
  const [desde, setDesde] = useState(() => (descuento?.valid_from ?? '').slice(0, 10));
  const [hasta, setHasta] = useState(() => (descuento?.valid_until ?? '').slice(0, 10));

  const clientes = catalogos.data?.clientes ?? [];
  const grados = catalogos.data?.grados ?? [];

  const vehiculosDelCliente = useMemo(
    () =>
      (catalogos.data?.vehiculos ?? []).filter(
        (vehiculo) => !clienteId || vehiculo.customer_id === Number(clienteId),
      ),
    [catalogos.data?.vehiculos, clienteId],
  );

  const porConsumo = tipo === 'consumption';
  const porPlaca = tipo === 'license_plate';
  const puedeGuardar = Boolean(clienteId && gradoId && importe) && !guardando;

  const cerrar = () => onClose();

  return (
    <Dialog
      open={open}
      onClose={cerrar}
      title={descuento ? `Editar descuento #${descuento.id}` : 'Nuevo descuento de la APP'}
      description="El descuento aplica al cliente de la aplicación en el producto y la vigencia indicados."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={cerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() =>
              onGuardar({
                customer_id: Number(clienteId),
                vehicle_id: porPlaca && vehiculoId ? Number(vehiculoId) : null,
                fuel_grade_id: Number(gradoId),
                discount_type: tipo,
                discount_amount: Number(importe),
                min_consumption: porConsumo && minimo ? Number(minimo) : null,
                max_consumption: porConsumo && maximo ? Number(maximo) : null,
                valid_from: desde || undefined,
                valid_until: hasta || null,
              })
            }
          >
            {descuento ? 'Guardar cambios' : 'Crear descuento'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Alert variant="error" title="No se pudo guardar el descuento">{mensajeDeError(error)}</Alert> : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs text-ink-500 sm:col-span-2">
            Cliente
            <Select
              className={CONTROL}
              value={clienteId}
              onChange={(evento) => {
                setClienteId(evento.target.value);
                setVehiculoId('');
              }}
              disabled={catalogos.isLoading}
              searchable
            >
              <option value="">Elige el cliente…</option>
              {clientes.map((cliente) => (
                <option key={cliente.id} value={String(cliente.id)} data-search={cliente.numero_doc}>
                  {nombreCliente(cliente, cliente.id)}
                  {cliente.numero_doc ? ` · ${cliente.numero_doc}` : ''}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-ink-500">
            Tipo de descuento
            <Select className={CONTROL} value={tipo} onChange={(evento) => setTipo(evento.target.value)}>
              {TIPOS.map((opcion) => (
                <option key={opcion.clave} value={opcion.clave}>
                  {opcion.label}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-ink-500">
            Producto
            <Select className={CONTROL} value={gradoId} onChange={(evento) => setGradoId(evento.target.value)}>
              <option value="">Elige el producto…</option>
              {grados.map((grado) => (
                <option key={grado.id} value={String(grado.id)}>
                  {grado.nombre}
                </option>
              ))}
            </Select>
          </label>

          {porPlaca ? (
            <label className="flex flex-col gap-1 text-xs text-ink-500 sm:col-span-2">
              Vehículo
              <Select
                className={CONTROL}
                value={vehiculoId}
                onChange={(evento) => setVehiculoId(evento.target.value)}
              >
                <option value="">Cualquier placa del cliente</option>
                {vehiculosDelCliente.map((vehiculo) => (
                  <option key={vehiculo.id} value={String(vehiculo.id)}>
                    {vehiculo.license_plate}
                    {vehiculo.vehicle_type ? ` · ${vehiculo.vehicle_type}` : ''}
                  </option>
                ))}
              </Select>
              <span className="text-xs text-ink-400">
                Un descuento sin vehículo aplica a cualquier placa del cliente.
              </span>
            </label>
          ) : null}

          <Input
            label="Importe del descuento"
            hint="Por galón, en soles. Descuento: valor positivo (ej. 0.23). Recargo: valor negativo (ej. -0.23)."
            type="number"
            step="0.0001"
            value={importe}
            onChange={(evento) => setImporte(evento.target.value)}
          />

          {porConsumo ? (
            <>
              <Input
                label="Consumo mínimo"
                hint="Opcional. Si se indica, el descuento aplica desde ese consumo."
                type="number"
                step="0.01"
                value={minimo}
                onChange={(evento) => setMinimo(evento.target.value)}
              />
              <Input
                label="Consumo máximo"
                hint="Opcional."
                type="number"
                step="0.01"
                value={maximo}
                onChange={(evento) => setMaximo(evento.target.value)}
              />
            </>
          ) : null}

          <Input
            label="Vigente desde"
            type="date"
            value={desde}
            onChange={(evento) => setDesde(evento.target.value)}
          />
          <Input
            label="Vigente hasta"
            hint="Vacío significa sin vencimiento."
            type="date"
            value={hasta}
            onChange={(evento) => setHasta(evento.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Vehículos de un cliente
 * ------------------------------------------------------------------ */

interface DialogoVehiculosProps {
  open: boolean;
  vehiculo: VehiculoApp | null;
  vehiculos: VehiculoAppDetalle[] | undefined;
  cargando: boolean;
  error: unknown;
  onCerrar: () => void;
  onEliminar: (vehiculoId: number) => void;
  onNuevo: () => void;
}

/**
 * Vehículos activos del cliente, con su alta y su baja.
 *
 * La edición del vehículo en el servicio externo sigue `bloqueada`; aquí se
 * administra el registro local de `customer_vehicles`.
 */
export function DialogoVehiculosDeCliente({
  open,
  vehiculo,
  vehiculos,
  cargando,
  error,
  onCerrar,
  onEliminar,
  onNuevo,
}: DialogoVehiculosProps) {
  return (
    <Dialog
      open={open}
      onClose={onCerrar}
      title={`Vehículos de ${vehiculo?.cliente ?? 'la APP'}`}
      description="Placas registradas para este cliente y sus descuentos asociados."
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCerrar}>
            Cerrar
          </Button>
          <Button onClick={onNuevo}>Agregar vehículo</Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error ? <Alert variant="error" title="No se pudieron cargar los vehículos">{mensajeDeError(error)}</Alert> : null}

        <div className="overflow-auto rounded-card border border-ink-100">
          <table className="w-full border-collapse text-[13px]">
            <thead className="bg-white">
              <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                <th className="px-3 py-2">Placa</th>
                <th className="px-3 py-2">Vehículo</th>
                <th className="px-3 py-2 text-right">Descuentos</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cargando && !vehiculos ? (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-sm text-ink-500">
                    Cargando vehículos…
                  </td>
                </tr>
              ) : null}

              {!cargando && (vehiculos ?? []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-sm text-ink-500">
                    El cliente no tiene vehículos activos.
                  </td>
                </tr>
              ) : null}

              {(vehiculos ?? []).map((item) => (
                <tr key={item.id} className="border-t border-ink-100">
                  <td className="px-3 py-2 font-semibold whitespace-nowrap text-ink-900">
                    {item.license_plate}
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-600">
                    {[item.vehicle_type, item.brand, item.model, item.year]
                      .filter(Boolean)
                      .join(' ') || 'sin detalle'}
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-ink-600">{item.descuentos}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEliminar(item.id)}
                      className={cn('text-rose-600 hover:border-rose-300 hover:bg-rose-50')}
                    >
                      Dar de baja
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-ink-500">
          Dar de baja un vehículo desactiva también los descuentos que apuntaban a esa placa.
        </p>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Alta y edición de un cliente de la APP
 * ------------------------------------------------------------------ */

interface DialogoClienteAppProps {
  open: boolean;
  cliente: ClienteAppGeneral | null;
  guardando: boolean;
  error: unknown;
  onCerrar: () => void;
  onGuardar: (datos: DatosClienteApp) => void;
}

/** Alta y edición del cliente de la APP (`ClientesDiscount::{crear_cliente, editar_cliente}`). */
export function DialogoClienteApp({
  open,
  cliente,
  guardando,
  error,
  onCerrar,
  onGuardar,
}: DialogoClienteAppProps) {
  const [tipoDoc, setTipoDoc] = useState('1');
  const [numeroDoc, setNumeroDoc] = useState('');
  const [nombre, setNombre] = useState('');
  const [nombreComercial, setNombreComercial] = useState('');
  const [direccion, setDireccion] = useState('');
  const [telefono, setTelefono] = useState('');
  const [correo, setCorreo] = useState('');
  const [withholding, setWithholding] = useState(false);
  const [porcentaje, setPorcentaje] = useState('');
  const [portalActivo, setPortalActivo] = useState(false);
  const [password, setPassword] = useState('');

  const consulta = useConsultaDocumento();
  const [avisoConsulta, setAvisoConsulta] = useState<string | null>(null);

  // Al abrirse rellena el formulario con el cliente en edición.
  const [firma, setFirma] = useState('');

  if (open && firma !== String(cliente?.id ?? 'nuevo')) {
    setFirma(String(cliente?.id ?? 'nuevo'));
    setTipoDoc(cliente?.tipo_doc || '1');
    setNumeroDoc(cliente?.numero_doc ?? '');
    setNombre(cliente?.nombre ?? '');
    setNombreComercial(cliente?.nombre_comercial ?? '');
    setDireccion(cliente?.direccion ?? '');
    setTelefono(cliente?.telefono ?? '');
    setCorreo(cliente?.correo ?? '');
    setWithholding(cliente?.withholding ?? false);
    setPorcentaje(cliente?.withholding_percentage != null ? String(cliente.withholding_percentage) : '');
    setPortalActivo(cliente?.portal_activo ?? false);
    setPassword('');
  }

  const yaTieneClave = cliente?.portal_tiene_clave ?? false;
  const passwordCorta = password.length > 0 && password.length < 6;
  // Reglas del panel anterior: activar el portal exige contraseña si el cliente
  // todavía no tenía ninguna; al editar, dejarla vacía significa «no cambiarla».
  const faltaPassword = portalActivo && !yaTieneClave && password.length === 0;

  const puedeGuardar =
    numeroDoc.trim().length > 0 &&
    nombre.trim().length > 0 &&
    !passwordCorta &&
    !faltaPassword &&
    !guardando;

  /** Consulta el documento en SUNAT/RENIEC (o en la cartera local) y rellena la razón social. */
  const buscarDocumento = () => {
    setAvisoConsulta(null);

    const numero = numeroDoc.trim();

    if (numero === '') {
      setAvisoConsulta('Escribe el número de documento antes de buscarlo.');

      return;
    }

    consulta.mutate(
      { numero, tipo: tipoDoc === '6' ? 'ruc' : 'dni' },
      {
        onSuccess: (respuesta) => {
          const documento = respuesta.documento;

          setNombre(documento.nombre ?? nombre);
          setNombreComercial(documento.nombre ?? nombreComercial);
          setDireccion(documento.direccion ?? direccion);

          if (respuesta.origen === 'varios') {
            setAvisoConsulta('Documento genérico de consumidor final: CLIENTES VARIOS.');

            return;
          }

          const detalle = [documento.estado, documento.condicion].filter(Boolean).join(' · ');

          setAvisoConsulta(
            `${respuesta.origen === 'local' ? 'Datos tomados de la cartera de clientes' : 'Datos obtenidos del servicio de consulta'}${
              detalle ? ` · ${detalle}` : ''
            }.`,
          );
        },
        onError: (fallo) =>
          setAvisoConsulta(
            fallo instanceof ApiError ? fallo.message : 'No se pudo consultar el documento.',
          ),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onCerrar}
      title={cliente ? 'Editar cliente de la APP' : 'Nuevo cliente de la APP'}
      description="El cliente se registra en el servicio que atiende la aplicación y, si se indica, se le habilita el acceso al portal del cliente."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() =>
              onGuardar({
                tipo_doc: tipoDoc,
                numero_doc: numeroDoc.trim(),
                nombre: nombre.trim(),
                nombre_comercial: nombreComercial.trim() || undefined,
                direccion: direccion.trim() || undefined,
                telefono: telefono.trim() || undefined,
                correo: correo.trim() || undefined,
                withholding,
                withholding_percentage: withholding ? Number(porcentaje) || 0 : 0,
                portal_activo: portalActivo,
                password: password.trim() || undefined,
              })
            }
          >
            {cliente ? 'Guardar cambios' : 'Crear cliente'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error ? <Alert variant="error" title="No se pudo guardar el cliente">{mensajeDeError(error)}</Alert> : null}

        {avisoConsulta ? (
          <Alert variant={consulta.isError ? 'error' : 'info'} title="Consulta de documento">
            {avisoConsulta}
          </Alert>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Tipo de documento</span>
            <Select className={CONTROL} value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value)}>
              <option value="1">DNI</option>
              <option value="6">RUC</option>
            </Select>
          </label>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Número de documento"
                value={numeroDoc}
                maxLength={11}
                onChange={(evento) => setNumeroDoc(evento.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mb-0.5"
              loading={consulta.isPending}
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              onClick={buscarDocumento}
            >
              Buscar
            </Button>
          </div>
          <Input label="Nombre / Razón social" value={nombre} onChange={(evento) => setNombre(evento.target.value)} />
          <Input label="Nombre comercial" value={nombreComercial} onChange={(evento) => setNombreComercial(evento.target.value)} />
          <Input label="Dirección" value={direccion} onChange={(evento) => setDireccion(evento.target.value)} />
          <Input label="Teléfono" value={telefono} onChange={(evento) => setTelefono(evento.target.value)} />
          <Input label="Correo" type="email" value={correo} onChange={(evento) => setCorreo(evento.target.value)} />
          <div className="flex flex-col gap-1.5 text-sm">
            <label className="flex items-center gap-2 font-medium text-ink-700">
              <input
                type="checkbox"
                className="size-4 rounded border-ink-300"
                checked={withholding}
                onChange={(evento) => setWithholding(evento.target.checked)}
              />
              Aplican retenciones
            </label>
            {withholding ? (
              <Input
                label="Porcentaje de retención"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={porcentaje}
                onChange={(evento) => setPorcentaje(evento.target.value)}
              />
            ) : null}
          </div>
        </div>

        {/* Acceso al portal del cliente: se guarda en la base local (el usuario
            es el número de documento), como en el panel anterior. */}
        <fieldset className="rounded-card border border-ink-200 p-3">
          <legend className="flex items-center gap-1.5 px-1 text-xs font-semibold tracking-wide text-ink-500 uppercase">
            <Globe className="size-3.5" aria-hidden="true" />
            Acceso al portal del cliente
          </legend>

          <label className="flex items-center gap-2 text-sm font-medium text-ink-700">
            <input
              type="checkbox"
              className="size-4 rounded border-ink-300"
              checked={portalActivo}
              onChange={(evento) => setPortalActivo(evento.target.checked)}
            />
            Permitir el acceso al portal
          </label>

          <p className="mt-1 text-xs text-ink-500">
            El usuario es el número de documento
            {numeroDoc ? ` (${numeroDoc})` : ''} y la contraseña se le entrega al cliente.
          </p>

          {portalActivo ? (
            <div className="mt-3">
              <Input
                label={cliente ? 'Nueva contraseña' : 'Contraseña'}
                type="password"
                value={password}
                autoComplete="new-password"
                error={passwordCorta ? 'Mínimo 6 caracteres.' : undefined}
                hint={
                  cliente
                    ? yaTieneClave
                      ? 'Dejar vacío para no cambiarla.'
                      : 'Obligatoria para activar el portal (mínimo 6 caracteres).'
                    : 'Mínimo 6 caracteres.'
                }
                onChange={(evento) => setPassword(evento.target.value)}
              />
            </div>
          ) : null}

          {cliente?.portal_activo ? (
            <p className="mt-2 text-xs text-emerald-700">
              Portal activo
              {cliente.portal_fecha_registro ? ` desde ${fechaCorta(cliente.portal_fecha_registro)}` : ''}
              {cliente.portal_ultimo_acceso ? ` · último acceso ${fechaCorta(cliente.portal_ultimo_acceso)}` : ' · sin accesos'}.
            </p>
          ) : null}
        </fieldset>
      </div>
    </Dialog>
  );
}
